import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { X, Star, Minus, Plus, Check, List, IndentIcon, AlignLeft, ListOrdered } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { UserContext } from '../../UserContext';
import TagSelector from './TagSelector';
import Tooltip from '../Tooltip';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentWorkspace } from '../../store/workspacesSlice';
import OfflineStatusBanner from '../../components/OfflineStatusBanner/OfflineStatusBanner';
import FormatTools from '../FormatTools/FormatTools';

const AddItemModal = ({ 
  isOpen, 
  onClose, 
  onAdd, 
  workspaceId, 
  baskets,
  defaultBasketId,
  initialTopicName
}) => {
  const dispatch = useDispatch();
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const workspaceTags = currentWorkspace.tags || [];

  const { user } = useContext(UserContext);
  const [topicName, setTopicName] = useState(initialTopicName || '');
  const [concepts, setConcepts] = useState([{ name: '', is_highlighted: true }]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedBasketId, setSelectedBasketId] = useState(defaultBasketId || null);
  const [showSuccessIndicator, setShowSuccessIndicator] = useState(false);
  const [highlightedConceptIndex, setHighlightedConceptIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [bulletPointModes, setBulletPointModes] = useState([]);
  const [numberedListModes, setNumberedListModes] = useState([]);
  const [listCounters, setListCounters] = useState([1]);
  const conceptRefs = useRef([]);
  const [activeConceptIndex, setActiveConceptIndex] = useState(0);
  const topicNameRef = useRef(null);

  useEffect(() => {
    if (isOpen && topicNameRef.current) {
      setTimeout(() => {
        topicNameRef.current.focus();
        const length = topicNameRef.current.value.length;
        topicNameRef.current.setSelectionRange(length, length);
      }, 50);
    }
  }, [isOpen]);

  useEffect(() => {
    // Only focus the last concept when explicitly adding a new one
    if (concepts.length > 1) {
      const lastConceptIndex = concepts.length - 1;
      const textarea = conceptRefs.current[lastConceptIndex];
      if (textarea) {
        textarea.focus();
      }
    }
  }, [concepts.length]);

  const handleTagSelect = useCallback((tag) => {
    console.log('Selected tag:', tag);
    setSelectedTag(tag);
  }, []);

  const handleTagSelected = () => {
    if (conceptRefs.current[0]) {
      conceptRefs.current[0].focus();
    }
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !user.id) {
      console.error('User not authenticated or user ID is missing');
      toast.error('Please log in to create an item.');
      return;
    }

    // Check for internet connection
    if (!navigator.onLine) {
      toast.error('No internet connection. Please check your connection and try again.');
      return;
    }

    setIsCreating(true);

    try {
      // Create the base topic data with proper tag handling
      const topicData = {
        name: topicName.trim(),
        basket_id: selectedBasketId || defaultBasketId,
        user_id: user.id,
        workspace_id: workspaceId,
        order_index: 0,
        concepts: concepts.map(concept => ({
          text: concept.name,
          is_highlighted: concept.is_highlighted
        })),
        tag_id: selectedTag?.id?.startsWith('new-') ? undefined : selectedTag?.id,
        tag: selectedTag?.id?.startsWith('new-') ? selectedTag : null
      };

      console.log('Submitting form with data:', topicData);

      if (typeof onAdd === 'function') {
        try {
          if (defaultBasketId) {
            await onAdd(topicData);
          } else {
            await onAdd({
              workspaceId,
              newTopic: topicData
            });
          }

          // Clear form fields EXCEPT the tag
          setTopicName('');
          setConcepts([{ name: '', is_highlighted: true }]);
          setSelectedBasketId(null);

          // Show success indicator
          setShowSuccessIndicator(true);
          setTimeout(() => {
            setShowSuccessIndicator(false);
            setIsCreating(false);
            if (topicNameRef.current) {
              topicNameRef.current.focus();
            }
          }, 1500);

        } catch (error) {
          // Handle specific error types
          if (error.message?.includes('network')) {
            toast.error('Network error. Please check your internet connection.');
          } else if (error.message?.includes('timeout')) {
            toast.error('Request timed out. Please try again.');
          } else if (error.code === '23505') { // Supabase unique constraint violation
            toast.error('A topic with this name already exists.');
          } else {
            toast.error(`Failed to create topic: ${error.message || 'Unknown error occurred'}`);
          }
          console.error('Error creating topic:', error);
          setIsCreating(false);
        }
      } else {
        console.error('onAdd is not a function');
        toast.error('System error: Unable to create topic');
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error(error.message || 'Failed to create topic. Please try again.');
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    // Clear all form fields
    setTopicName('');
    setConcepts([{ name: '', is_highlighted: true }]);
    setSelectedTag(null); // Clear the tag when closing the modal
    setSelectedBasketId(null);
    setHighlightedConceptIndex(0); // Reset highlighted index to 0
    onClose();
  };

  const handleConceptChange = (index, field, value) => {
    const updatedConcepts = [...concepts];
    if (field === 'is_highlighted') {
      // If we're setting a concept as highlighted, unset any previously highlighted concept
      updatedConcepts.forEach((c, i) => {
        c.is_highlighted = i === index;
      });
      setHighlightedConceptIndex(index);
    } else {
      updatedConcepts[index][field] = value;
    }
    setConcepts(updatedConcepts);
  };

  const addConcept = () => {
    const newIndex = concepts.length;
    setConcepts([...concepts, { name: '', is_highlighted: false }]);
    
    setTimeout(() => {
      if (conceptRefs.current[newIndex]) {
        conceptRefs.current[newIndex].focus();
      }
    }, 0);
  };

  const removeConcept = (index) => {
    const updatedConcepts = concepts.filter((_, i) => i !== index);
    setConcepts(updatedConcepts);
  };

  const formatSelection = (index, type, e) => {
    e?.preventDefault();
    const textarea = conceptRefs.current[index];
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    
    if (start === end) {
      // No selection, add formatting at cursor position
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      let formattedText;
      switch (type) {
        case 'bullet':
          formattedText = `${textBeforeCursor}• ${textAfterCursor}`;
          break;
        case 'number':
          const newCounter = (listCounters[index] || 0) + 1;
          formattedText = `${textBeforeCursor}${newCounter}. ${textAfterCursor}`;
          const newCounters = [...listCounters];
          newCounters[index] = newCounter;
          setListCounters(newCounters);
          break;
        case 'indent':
          formattedText = `${textBeforeCursor}    ${textAfterCursor}`;
          break;
        default:
          formattedText = text;
      }
      
      handleConceptChange(index, 'name', formattedText);
      
      // Set cursor position after the inserted formatting
      setTimeout(() => {
        const newPosition = cursorPosition + (type === 'bullet' ? 2 : type === 'number' ? 3 : 4);
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
      return;
    }

    // Get selected text and split into lines
    const selectedText = text.substring(start, end);
    const lines = selectedText.split('\n');
    
    // Format each line based on type
    const formattedLines = lines.map((line, i) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return '';
      
      switch (type) {
        case 'bullet':
          return line.startsWith('• ') ? line : `• ${trimmedLine}`;
        case 'number':
          return line.match(/^\d+\. /) ? line : `${i + 1}. ${trimmedLine}`;
        case 'indent':
          return line.startsWith('    ') ? line : `    ${trimmedLine}`;
        default:
          return line;
      }
    });

    // Combine the formatted lines
    const formattedText = formattedLines.join('\n');
    
    // Replace the selected text with formatted text
    const newText = text.substring(0, start) + formattedText + text.substring(end);
    handleConceptChange(index, 'name', newText);

    // Maintain focus and update selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start,
        start + formattedText.length
      );
    }, 0);
  };

  const addBulletPoint = (index) => {
    const newModes = [...bulletPointModes];
    newModes[index] = !newModes[index];
    setBulletPointModes(newModes);
    
    const newNumberedModes = [...numberedListModes];
    newNumberedModes[index] = false;
    setNumberedListModes(newNumberedModes);

    if (newModes[index]) {
      const textarea = conceptRefs.current[index];
      if (!textarea) return;

      const cursorPosition = textarea.selectionStart;
      const text = textarea.value;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      const formattedText = `${textBeforeCursor}• ${textAfterCursor}`;
      handleConceptChange(index, 'name', formattedText);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
      }, 0);
    }
  };

  const addNumberedList = (index) => {
    const newModes = [...numberedListModes];
    newModes[index] = !newModes[index];
    setNumberedListModes(newModes);
    
    const newBulletModes = [...bulletPointModes];
    newBulletModes[index] = false;
    setBulletPointModes(newBulletModes);

    if (newModes[index]) {
      const textarea = conceptRefs.current[index];
      if (!textarea) return;

      const cursorPosition = textarea.selectionStart;
      const text = textarea.value;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      const newCounter = 1;
      const formattedText = `${textBeforeCursor}${newCounter}. ${textAfterCursor}`;
      handleConceptChange(index, 'name', formattedText);
      
      const newCounters = [...listCounters];
      newCounters[index] = newCounter;
      setListCounters(newCounters);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPosition + 3, cursorPosition + 3);
      }, 0);
    }
  };

  const addIndentation = (index) => formatSelection(index, 'indent', { preventDefault: () => {} });
  const addParagraph = (index) => formatSelection(index, 'paragraph', { preventDefault: () => {} });

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      if (bulletPointModes[index]) {
        e.preventDefault();
        insertFormatting(index, '\n• ');
      } else if (numberedListModes[index]) {
        e.preventDefault();
        const newCounters = [...listCounters];
        newCounters[index] = (newCounters[index] || 0) + 1;
        setListCounters(newCounters);
        insertFormatting(index, `\n${newCounters[index]}. `);
      }
    }
  };

  const insertFormatting = (index, startChar, endChar = '') => {
    const textarea = conceptRefs.current[index];
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const beforeText = text.substring(0, start);
    const selectedText = text.substring(start, end);
    const afterText = text.substring(end);

    const newText = beforeText + startChar + selectedText + endChar + afterText;
    handleConceptChange(index, 'name', newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + startChar.length, end + startChar.length);
    }, 0);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 sm:p-6">
      <div className="bg-neutral-50 rounded-xl border-2 border-neutral-900 shadow-xl p-5 sm:p-6 
                    w-full max-w-3xl max-h-[90vh] overflow-y-auto relative">
        <OfflineStatusBanner />
        {showSuccessIndicator && (
          <div className="absolute inset-0 flex items-center justify-center bg-emerald-600 bg-opacity-90 z-[60]">
            <div className="bg-white rounded-xl border-2 border-neutral-900 p-4 flex items-center shadow-lg">
              <Check size={24} className="text-emerald-600 mr-2" />
              <span className="text-neutral-900 font-medium">Topic created successfully</span>
            </div>
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-neutral-900">Create Item</h2>
          <button 
            onClick={handleClose} 
            className="text-neutral-900 hover:text-neutral-700 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Topic Name */}
          <div>
            <label htmlFor="topicName" className="block text-sm font-semibold text-neutral-800 mb-2">
              Topic Name
            </label>
            <input
              id="topicName"
              ref={topicNameRef}
              type="text"
              value={topicName}
              onChange={(e) => setTopicName(e.target.value)}
              className="w-full px-3 py-2 bg-white border-2 border-neutral-900 rounded-xl text-neutral-900 
                       placeholder-neutral-400"
              placeholder="e.g., Electron"
              required
            />
          </div>

          {/* Tag Selector */}
          <div>
            <label className="block text-sm font-semibold text-neutral-800 mb-2">Tag</label>
            <TagSelector
              onTagSelect={handleTagSelect}
              selectedTag={selectedTag}
              existingTags={workspaceTags}
              onTagSelected={handleTagSelected}
            />
          </div>

          {/* Concepts */}
          <div>
            <label className="block text-sm font-semibold text-neutral-800 mb-2">Concepts</label>
            
            <div className="space-y-4">
              {concepts.map((concept, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-grow relative">
                    <textarea
                      ref={el => conceptRefs.current[index] = el}
                      value={concept.name}
                      onChange={(e) => handleConceptChange(index, 'name', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className="w-full px-4 py-3 pr-24 bg-white border-2 border-neutral-900 rounded-xl 
                       text-neutral-900 placeholder-neutral-400 resize-y min-h-[120px]"
                      placeholder={`Concept ${index + 1}`}
                      required
                    />
                    <FormatTools
                      onBulletPoint={() => addBulletPoint(index)}
                      onNumberedList={() => addNumberedList(index)}
                      onIndentation={() => addIndentation(index)}
                      onParagraph={() => addParagraph(index)}
                      bulletPointActive={bulletPointModes[index]}
                      numberedListActive={numberedListModes[index]}
                      conceptIndex={index}
                      onFormatSelection={(type, e) => formatSelection(index, type, e)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleConceptChange(index, 'is_highlighted', !concept.is_highlighted)}
                    className="p-2 rounded-full hover:bg-yellow-100 transition-colors mt-2"
                    title={concept.is_highlighted ? "Remove importance" : "Mark as most important"}
                  >
                    <Star
                      size={20}
                      className={`transition-colors ${
                        concept.is_highlighted
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-400 hover:text-yellow-400'
                      }`}
                    />
                  </button>
                  {concepts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeConcept(index)}
                      className="p-2 bg-red-600 text-white rounded-xl border-2 border-neutral-900 
                       hover:bg-red-700 transition-colors mt-2"
                      aria-label="Remove concept"
                    >
                      <Minus size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addConcept}
              className="mt-3 flex items-center text-emerald-600 hover:text-emerald-700 
                       transition-colors font-medium"
            >
              <Plus size={16} className="mr-1" /> Add Concept
            </button>
          </div>

          {/* Basket Selection */}
          <div>
            <label className="block text-sm font-semibold text-neutral-800 mb-2">
              Basket {defaultBasketId ? '(Pre-selected)' : '(Optional)'}
            </label>
            <select
              value={selectedBasketId || ''}
              onChange={(e) => setSelectedBasketId(e.target.value || null)}
              className="w-full px-3 py-2 bg-white border-2 border-neutral-900 rounded-xl text-neutral-900"
              disabled={!!defaultBasketId}
            >
              <option value="">Select a basket</option>
              {baskets.map((basket) => (
                <option key={basket.id} value={basket.id}>
                  {basket.name}
                </option>
              ))}
            </select>
            {baskets.length === 0 && (
              <p className="text-sm text-cranberry-700 mt-1">No baskets available for this workspace.</p>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            {!isCreating && (
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-white text-neutral-900 rounded-xl border-2 border-neutral-900 
                         font-medium hover:bg-neutral-100 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 
                       font-medium hover:bg-cranberry-600 transition-colors disabled:opacity-70 
                       disabled:cursor-not-allowed"
              disabled={!user || !user.id || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default React.memo(AddItemModal);
